import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  MessageSquareText,
  AlertCircle,
  Gauge,
  ThumbsUp,
  TrendingUp,
  Activity,
  Library,
  Languages,
  ClipboardList,
  Award,
  Mic,
  Type,
  MousePointerClick,
} from "lucide-react";
import { dashboardApi, alphaDashboardApi } from "../api/client";
import {
  fmtNumber,
  fmtPct,
  fmtMs,
  fmtDateShort,
  RANGE_PRESETS,
  rangeToParams,
  langLabel,
  themeLabel,
  CHART_COLORS,
} from "../utils/format";

// -- helpers ------------------------------------------------------

const Skeleton = ({
  width = "100%",
  height = 20,
  borderRadius = 4,
  style = {},
}) => (
  <div
    className="skeleton-box"
    style={{ width, height, borderRadius, ...style }}
  />
);

const StatCard = ({ icon: Icon, label, value, sublabel, accent, loading }) => (
  <div
    className="panel"
    style={{
      padding: "24px",
      borderRadius: 16,
      background: "#ffffff",
      border: "1px solid var(--ink-100)",
      boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.03)",
      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
      cursor: "default",
      position: "relative",
      overflow: "hidden",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-4px)";
      e.currentTarget.style.boxShadow = "0 12px 24px -8px rgba(0, 0, 0, 0.08)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 4px 20px -4px rgba(0, 0, 0, 0.03)";
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        marginBottom: 16,
      }}
    >
      <div
        className="t-eyebrow"
        style={{ fontSize: 11, color: "var(--ink-500)", letterSpacing: "0.04em" }}
      >
        {label}
      </div>
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: accent || "var(--saffron-tint)",
          color: accent ? "inherit" : "var(--saffron-deep)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 8px -2px rgba(0,0,0,0.05)",
        }}
      >
        <Icon size={18} strokeWidth={2} />
      </div>
    </div>

    {loading ? (
      <Skeleton width="50%" height={40} style={{ marginBottom: 4 }} />
    ) : (
      <div
        style={{
          fontFamily: "var(--serif)",
          fontSize: 40,
          fontWeight: 500,
          letterSpacing: "-0.02em",
          lineHeight: 1,
          color: "var(--ink-900)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
    )}

    {loading ? (
      <Skeleton width="70%" height={14} style={{ marginTop: 12 }} />
    ) : sublabel ? (
      <div style={{ fontSize: 12, color: "var(--ink-500)", marginTop: 12, fontWeight: 500 }}>
        {sublabel}
      </div>
    ) : null}
  </div>
);

const Section = ({ title, eyebrow, children, right }) => (
  <div
    className="panel"
    style={{
      marginTop: 32,
      background: "#ffffff",
      borderRadius: 16,
      border: "1px solid var(--ink-100)",
      boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
      overflow: "visible",
    }}
  >
    <div
      className="panel-header"
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: 16,
        padding: "24px 28px",
        borderBottom: "1px solid var(--ink-100)",
        background: "rgba(251, 246, 238, 0.3)",
      }}
    >
      <div>
        {eyebrow && (
          <div className="t-eyebrow" style={{ marginBottom: 6, color: "var(--ink-500)" }}>
            {eyebrow}
          </div>
        )}
        <div className="t-display" style={{ fontSize: 24, color: "var(--ink-900)" }}>
          {title}
        </div>
      </div>
      {right && <div style={{ paddingBottom: 4 }}>{right}</div>}
    </div>
    <div className="panel-body" style={{ padding: "28px" }}>
      {children}
    </div>
  </div>
);

const Empty = ({ children }) => (
  <div
    style={{
      padding: "48px 24px",
      textAlign: "center",
      color: "var(--ink-400)",
      fontFamily: "var(--serif)",
      fontStyle: "italic",
      fontSize: 16,
      background: "var(--paper-2)",
      borderRadius: 12,
      border: "1px dashed var(--ink-200)",
    }}
  >
    {children}
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  if (typeof document === "undefined") return null;

  return (
    <div
      style={{
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "1px solid var(--ink-200)",
        borderRadius: 12,
        padding: "12px 16px",
        fontSize: 13,
        boxShadow: "0 8px 32px -8px rgba(0, 0, 0, 0.12)",
        pointerEvents: "none",
      }}
    >
      {label && (
        <div
          style={{
            fontWeight: 600,
            marginBottom: 8,
            fontFamily: "var(--serif)",
            color: "var(--ink-900)",
            fontSize: 14,
          }}
        >
          {label}
        </div>
      )}
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 4 }}>
          <span
            style={{
              width: 10,
              height: 10,
              background: p.color,
              borderRadius: 3,
              boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
            }}
          />
          <span style={{ color: "var(--ink-600)", fontWeight: 500 }}>{p.name}:</span>
          <span style={{ color: "var(--ink-900)", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
            {typeof p.value === "number" ? fmtNumber(p.value) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

// -- Method (text/voice) donut panel ----------------------------

const MethodDonut = ({ stats, loading }) => {
  const methodData = useMemo(() => {
    if (!stats) return [];
    return [
      { name: "Text", value: stats.textSessions || 0, color: "#4A90D9" },
      { name: "Voice", value: stats.voiceSessions || 0, color: "#C26C1B" },
    ];
  }, [stats]);

  const total = stats?.totalSessions || 0;

  return (
    <div
      className="panel"
      style={{
        background: "#ffffff",
        borderRadius: 16,
        border: "1px solid var(--ink-100)",
        boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
        overflow: "visible",
      }}
    >
      <div
        className="panel-header"
        style={{
          padding: "24px 28px",
          borderBottom: "1px solid var(--ink-100)",
          background: "rgba(251, 246, 238, 0.3)",
        }}
      >
        <div className="t-eyebrow" style={{ color: "var(--ink-500)", marginBottom: 6 }}>
          Madhyam · Channel
        </div>
        <div
          className="t-display"
          style={{ fontSize: 24, display: "flex", alignItems: "center", gap: 10, color: "var(--ink-900)" }}
        >
          <Activity size={22} style={{ color: "var(--saffron-deep)" }} />
          Text vs Voice
        </div>
      </div>
      <div className="panel-body" style={{ padding: "32px" }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: 300 }}>
            <Skeleton width={220} height={220} borderRadius="50%" />
          </div>
        ) : !total ? (
          <Empty>No session data yet.</Empty>
        ) : (
          <>
            <div style={{ width: "100%", height: 250, position: "relative", marginBottom: 24 }}>
              <div style={{ position: "relative", zIndex: 2, width: "100%", height: "100%" }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={methodData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={72}
                      outerRadius={108}
                      paddingAngle={6}
                      strokeWidth={0}
                      cornerRadius={10}
                    >
                      {methodData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Legend wrapperStyle={{ fontSize: 13, fontWeight: 500, marginTop: 14 }} iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* Centre label */}
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -58%)",
                  textAlign: "center",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              >
                <div style={{ fontFamily: "var(--serif)", fontSize: 30, fontWeight: 700, color: "var(--ink-900)", lineHeight: 1 }}>
                  {fmtNumber(total)}
                </div>
                <div style={{ fontSize: 12, color: "var(--ink-500)", marginTop: 8, fontWeight: 600, letterSpacing: "0.12em" }}>
                  SESSIONS
                </div>
              </div>
            </div>

            {/* Stat pills */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 18 }}>
              {[
                { icon: Type, label: "Text", value: stats?.textSessions || 0, color: "#4A90D9", bg: "#EBF4FF" },
                { icon: Mic, label: "Voice", value: stats?.voiceSessions || 0, color: "#C26C1B", bg: "#FAEEDA" },
              ].map(({ icon: Icon, label, value, color, bg }) => (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "18px 20px",
                    borderRadius: 16,
                    background: bg,
                    border: `1px solid ${color}22`,
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: `${color}22`,
                      color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={18} strokeWidth={2} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "var(--ink-500)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.12em" }}>
                      {label}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "var(--ink-900)", fontVariantNumeric: "tabular-nums", marginTop: 4 }}>
                      {fmtNumber(value)}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--ink-500)", marginTop: 5 }}>
                      {total ? fmtPct(value / total) : "0%"} of total
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// -- main page ---------------------------------------------------

const PERSONA_META = {
  P1: { color: "#C26C1B", bg: "#FAEEDA" },
  P2: { color: "#0F6E56", bg: "#E1F5EE" },
  P3: { color: "#185FA5", bg: "#E6F1FB" },
  P4: { color: "#993356", bg: "#FBEAF0" },
  P5: { color: "#534AB7", bg: "#EEEDFE" },
};

export default function Dashboard() {
  const [testerType, setTesterType] = useState("beta");
  const [range, setRange] = useState("30d");

  const [headline, setHeadline] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [topics, setTopics] = useState([]);
  const [themes, setThemes] = useState([]);
  const [kmsRefs, setKmsRefs] = useState([]);
  const [unanswered, setUnanswered] = useState([]);
  const [evalSummary, setEvalSummary] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAlpha = testerType === "alpha";
  const api = isAlpha ? alphaDashboardApi : dashboardApi;

  const personaData = useMemo(() => {
    if (!evalSummary?.persona_performance) return [];
    return [...evalSummary.persona_performance].sort((a, b) => b.preferred_rate - a.preferred_rate);
  }, [evalSummary]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEvalSummary(null);
    setStats(null);

    const params = rangeToParams(range);

    const calls = [
      api.headline(params),
      api.timeseries(params),
      api.topics({ ...params, limit: 12 }),
      api.themes(params),
      api.kmsRefs({ ...params, limit: 8 }),
      api.unanswered({ ...params, limit: 6 }),
      ...(isAlpha
        ? [
          alphaDashboardApi.evaluationsSummary(params),
          alphaDashboardApi.stats(params),
        ]
        : []),
    ];

    Promise.all(calls)
      .then(([h, ts, tp, th, kr, un, ev, st]) => {
        if (cancelled) return;
        setHeadline(h);
        setTimeseries(ts);
        setTopics(tp);
        setThemes(th);
        setKmsRefs(kr);
        setUnanswered(un);
        if (isAlpha && ev) setEvalSummary(ev);
        if (isAlpha && st) setStats(st);
      })
      .catch((err) => !cancelled && setError(err?.response?.data?.error || err.message))
      .finally(() => !cancelled && setLoading(false));

    return () => { cancelled = true; };
  }, [range, testerType]);

  const langData = useMemo(() => {
    if (!headline?.language_distribution) return [];
    return Object.entries(headline.language_distribution).map(([k, v]) => ({
      name: langLabel(k),
      value: v,
      code: k,
    }));
  }, [headline]);

  const totalLangSessions = useMemo(
    () => langData.reduce((sum, item) => sum + item.value, 0),
    [langData]
  );

  const topLanguages = useMemo(
    () => [...langData].sort((a, b) => b.value - a.value).slice(0, 2),
    [langData]
  );

  const preferredData = useMemo(() => {
    if (!evalSummary?.preferred_distribution) return [];
    return Object.entries(evalSummary.preferred_distribution)
      .map(([label, count]) => ({ label: `Response ${label}`, count }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [evalSummary]);

  return (
    <div style={{ width: "100%" }}>
      <style>{`
        @keyframes skeleton-loading {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .skeleton-box {
          background: linear-gradient(90deg, var(--ink-100, #f3f4f6) 25%, var(--ink-200, #e5e7eb) 50%, var(--ink-100, #f3f4f6) 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s infinite linear;
        }
      `}</style>

      {/* Hero */}
      <div style={{ position: "relative", marginBottom: 32, padding: "16px 0" }}>
        <div
          className="devanagari-watermark"
          style={{
            position: "absolute",
            top: -30,
            right: 20,
            fontSize: 180,
            background: "linear-gradient(135deg, rgba(194,108,27,0.08) 0%, rgba(194,108,27,0.01) 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            pointerEvents: "none",
            userSelect: "none",
            fontWeight: 800,
          }}
        >
          ज्ञान
        </div>
        <div className="t-eyebrow" style={{ color: "var(--ink-500)", letterSpacing: "0.05em" }}>
          Sangathana Console / Dashboard
        </div>
        <h1
          className="t-display"
          style={{ fontSize: 36, marginTop: 8, marginBottom: 12, color: "var(--ink-900)" }}
        >
          The state of the{" "}
          <em style={{ color: "var(--saffron-deep)", fontStyle: "italic" }}>Dyanakosh</em>.
        </h1>
        <p
          style={{
            color: "var(--ink-600)",
            fontFamily: "var(--serif)",
            fontSize: 16,
            fontStyle: "italic",
            maxWidth: 720,
            lineHeight: 1.5,
          }}
        >
          A quiet glance at conversations, gaps in our corpus, and the rhythm of seekers across the platform.
        </p>
      </div>

      {/* Controls row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 32,
        }}
      >
        {/* Tester type toggle */}
        {(() => {
          const TABS = [
            { key: "beta", label: "Beta Testers" },
            { key: "alpha", label: "Alpha Testers" },
          ];
          const activeIdx = TABS.findIndex((t) => t.key === testerType);
          return (
            <div
              style={{
                display: "inline-flex",
                position: "relative",
                background: "var(--paper-2)",
                border: "1px solid var(--ink-200)",
                borderRadius: 12,
                padding: 4,
                boxShadow: "inset 0 2px 4px rgba(0,0,0,0.02)",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 4, bottom: 4, left: 4,
                  width: "calc(50% - 4px)",
                  background: "var(--saffron)",
                  borderRadius: 8,
                  transform: `translateX(${activeIdx * 100}%)`,
                  transition: "transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1)",
                  boxShadow: "0 2px 8px rgba(194,108,27,0.35), 0 1px 2px rgba(194,108,27,0.2)",
                }}
              />
              {TABS.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setTesterType(key)}
                  style={{
                    position: "relative",
                    zIndex: 1,
                    padding: "8px 24px",
                    fontSize: 13,
                    fontWeight: 600,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    borderRadius: 8,
                    color: testerType === key ? "#fff" : "var(--ink-600)",
                    transition: "color 250ms ease",
                    letterSpacing: "0.02em",
                    whiteSpace: "nowrap",
                    userSelect: "none",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          );
        })()}

        {/* Range selector */}
        <div
          style={{
            display: "inline-flex",
            background: "var(--paper-2)",
            padding: 4,
            borderRadius: 12,
            border: "1px solid var(--ink-200)",
            boxShadow: "inset 0 2px 4px rgba(0,0,0,0.02)",
          }}
        >
          {Object.entries(RANGE_PRESETS).map(([k, p]) => (
            <button
              key={k}
              onClick={() => setRange(k)}
              style={{
                padding: "8px 16px",
                fontSize: 13,
                background: range === k ? "#ffffff" : "transparent",
                border: "none",
                borderRadius: 8,
                color: range === k ? "var(--saffron-deep)" : "var(--ink-600)",
                fontWeight: range === k ? 600 : 500,
                boxShadow: range === k ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div
          style={{
            padding: "16px 20px",
            borderRadius: 12,
            border: "1px solid var(--red)",
            background: "#fff1f2",
            color: "var(--red)",
            marginBottom: 24,
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontWeight: 500,
          }}
        >
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* ── Headline stat cards ─────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gap: 20,
          gridTemplateColumns: `repeat(${isAlpha ? 5 : 6}, minmax(190px, 1fr))`,
          overflowX: "auto",
          paddingBottom: 8,
        }}
      >
        <StatCard
          icon={MessageSquareText}
          label="Conversations"
          value={fmtNumber(headline?.total_conversations || 0)}
          sublabel={headline ? `${fmtNumber(headline.total_messages || 0)} messages exchanged` : ""}
          loading={loading}
        />
        <StatCard
          icon={Gauge}
          label="Average Latency"
          value={fmtMs(headline?.avg_latency_ms || 0)}
          sublabel={headline ? `${fmtMs(headline.p95_latency_ms || 0)} total latency` : ""}
          accent="#f0f9ff"
          loading={loading}
        />
        {!isAlpha && (<StatCard
          icon={ThumbsUp}
          label="Feedback Score"
          value={
            (headline?.feedback_score >= 0 ? "+" : "") +
            fmtNumber(headline?.feedback_score || 0)
          }
          sublabel={
            headline
              ? `↑ ${fmtNumber(headline.positive_feedback || 0)}    ↓ ${fmtNumber(headline.negative_feedback || 0)}`
              : ""
          }
          accent="#f0fdf4"
          loading={loading}
        />
        )}
        {!isAlpha && (
          <StatCard
            icon={Award}
            label="Average Rating"
            value={headline?.avg_rating != null ? headline.avg_rating.toFixed(2) : "0.00"}
            sublabel={headline ? `${fmtNumber(headline?.rated_messages || 0)} responses rated` : ""}
            accent="#fff7ed"
            loading={loading}
          />
        )}
        {!isAlpha && (
          <StatCard
            icon={Activity}
            label="Rated Responses"
            value={fmtNumber(headline?.rated_messages || 0)}
            sublabel={headline ? `of ${fmtNumber(headline?.assistant_messages || 0)} assistant answers` : ""}
            accent="#eef2ff"
            loading={loading}
          />
        )}
        {!isAlpha && (
          <StatCard
            icon={MousePointerClick}
            label="Answer Detail Clicks"
            value={fmtNumber(headline?.answer_detail_clicks || 0)}
            sublabel="Unique detailed-answer expansions"
            accent="#f5f3ff"
            loading={loading}
          />
        )}
        {/* Alpha-only: evaluations card */}
        {isAlpha && (
          <StatCard
            icon={ClipboardList}
            label="Evaluations"
            value={fmtNumber(evalSummary?.total_evaluations || 0)}
            sublabel={
              evalSummary?.preferred_distribution
                ? `A: ${evalSummary.preferred_distribution.A || 0}  B: ${evalSummary.preferred_distribution.B || 0}  C: ${evalSummary.preferred_distribution.C || 0} preferred`
                : ""
            }
            accent="#fdf4ff"
            loading={loading}
          />
        )}

        {/* Alpha-only: text sessions */}
        {isAlpha && (
          <StatCard
            icon={Type}
            label="Text Sessions"
            value={fmtNumber(stats?.textSessions || 0)}
            sublabel={
              stats && stats.totalSessions
                ? `${fmtPct((stats.textSessions || 0) / stats.totalSessions)} of all sessions`
                : ""
            }
            accent="#EBF4FF"
            loading={loading}
          />
        )}

        {/* Alpha-only: voice sessions */}
        {isAlpha && (
          <StatCard
            icon={Mic}
            label="Voice Sessions"
            value={fmtNumber(stats?.voiceSessions || 0)}
            sublabel={
              stats && stats.totalSessions
                ? `${fmtPct((stats.voiceSessions || 0) / stats.totalSessions)} of all sessions`
                : ""
            }
            accent="#FAEEDA"
            loading={loading}
          />
        )}
      </div>

      {/* ── Alpha: avg ratings + preferred distribution ─────────── */}
      {isAlpha && (loading || evalSummary?.avg_ratings?.length > 0) && (
        <Section title="Average rating per response" eyebrow="Alpha evaluation">
          {loading ? (
            <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
              {[...Array(3)].map((_, i) => (
                <div key={i} style={{ flex: "1 1 200px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <Skeleton width="40%" height={16} />
                    <Skeleton width="30%" height={16} />
                  </div>
                  <Skeleton height={10} borderRadius={5} />
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
              {evalSummary.avg_ratings.map((r) => {
                const COLORS = { A: "#C26C1B", B: "#4A6741", C: "#1A3A5C" };
                const color = COLORS[r.label] || "var(--saffron)";
                const pct = (r.avg_rating / 5) * 100;
                return (
                  <div key={r.label} style={{ flex: "1 1 200px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 14 }}>
                      <span style={{ fontWeight: 600, color }}>Response {r.label}</span>
                      <span style={{ color: "var(--ink-700)", fontWeight: 500 }}>
                        {r.avg_rating.toFixed(2)} / 5
                        <span style={{ color: "var(--ink-400)", fontSize: 12, marginLeft: 8, fontWeight: 400 }}>
                          ({fmtNumber(r.count)} rated)
                        </span>
                      </span>
                    </div>
                    <div style={{ height: 10, background: "var(--ink-100)", borderRadius: 5, overflow: "hidden" }}>
                      <div
                        style={{
                          height: "100%",
                          width: pct + "%",
                          background: color,
                          borderRadius: 5,
                          transition: "width 600ms cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Preferred distribution bar */}
          {loading ? (
            <div style={{ marginTop: 36, paddingTop: 32, borderTop: "1px dashed var(--ink-200)" }}>
              <Skeleton width="40%" height={20} style={{ marginBottom: 20 }} />
              <Skeleton height={220} borderRadius={8} />
            </div>
          ) : (
            preferredData.length > 0 && (
              <div style={{ marginTop: 36, paddingTop: 32, borderTop: "1px dashed var(--ink-200)" }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-800)", marginBottom: 20 }}>
                  Times preferred as best response
                </div>
                <div style={{ width: "100%", height: 220 }}>
                  <ResponsiveContainer>
                    <BarChart data={preferredData} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--ink-100)" vertical={false} strokeOpacity={0.7} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12, fill: "var(--ink-600)", fontWeight: 500 }}
                        axisLine={false} tickLine={false} dy={8}
                      />
                      <YAxis
                        tick={{ fontSize: 12, fill: "var(--ink-400)" }}
                        axisLine={false} tickLine={false} dx={-8}
                      />
                      <Tooltip content={<ChartTooltip />} cursor={{ fill: "var(--paper-2)", opacity: 0.6 }} />
                      <Bar dataKey="count" name="Times preferred" radius={[6, 6, 0, 0]} maxBarSize={60}>
                        {preferredData.map((entry, i) => {
                          const COLORS = { "Response A": "#C26C1B", "Response B": "#4A6741", "Response C": "#1A3A5C" };
                          return <Cell key={i} fill={COLORS[entry.label] || "var(--saffron)"} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )
          )}
        </Section>
      )}

      {/* ── Alpha: persona performance ───────────────────────────── */}
      {isAlpha && (loading || personaData.length > 0) && (
        <Section title="Persona performance" eyebrow="Alpha evaluation">
          {loading ? (
            <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} width="100%" height={180} borderRadius={18} />
              ))}
            </div>
          ) : (
            <>
              {personaData[0] && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    marginBottom: 24,
                    padding: "18px 22px",
                    borderRadius: 18,
                    background: "rgba(194, 108, 27, 0.08)",
                    border: "1px solid rgba(194, 108, 27, 0.18)",
                  }}
                >
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                      <Award size={20} color="var(--saffron-deep)" />
                      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ink-900)" }}>
                        Most preferred persona
                      </div>
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: "var(--ink-900)", marginBottom: 4 }}>
                      {personaData[0].persona_name}
                    </div>
                    <div style={{ color: "var(--ink-700)", fontSize: 13, fontWeight: 600 }}>
                      {fmtNumber(personaData[0].total_responses)} responses · {(personaData[0].preferred_rate * 100).toFixed(1)}% preferred
                    </div>
                  </div>
                  <div style={{ minWidth: 180, textAlign: "right" }}>
                    <div style={{ fontSize: 12, color: "var(--ink-500)", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 8 }}>
                      Top persona score
                    </div>
                    <div style={{ fontSize: 32, fontWeight: 800, color: "var(--saffron-deep)", lineHeight: 1 }}>
                      {(personaData[0].avg_rating || 0).toFixed(2)} / 5
                    </div>
                    <div style={{ fontSize: 13, color: "var(--ink-500)", marginTop: 6 }}>
                      Avg rating across {fmtNumber(personaData[0].total_responses)} responses
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
                {personaData.map((p) => {
                  const meta = PERSONA_META[p.persona_id] || { color: "#888", bg: "#eee" };
                  const isTop = p.persona_id === personaData[0]?.persona_id;
                  return (
                    <div
                      key={p.persona_id}
                      style={{
                        background: "#ffffff",
                        borderRadius: 18,
                        padding: 22,
                        border: `1px solid ${isTop ? "rgba(194, 108, 27, 0.18)" : "var(--ink-100)"}`,
                        boxShadow: isTop ? "0 16px 40px -22px rgba(194, 108, 27, 0.45)" : "0 8px 24px -16px rgba(0, 0, 0, 0.06)",
                        transition: "transform 200ms ease, box-shadow 200ms ease",
                      }}
                      onMouseEnter={(e) => {
                        if (!isTop) {
                          e.currentTarget.style.transform = "translateY(-3px)";
                          e.currentTarget.style.boxShadow = "0 16px 36px -20px rgba(0, 0, 0, 0.08)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isTop) {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 8px 24px -16px rgba(0, 0, 0, 0.06)";
                        }
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink-900)" }}>{p.persona_name}</div>
                            <div style={{ fontSize: 12, color: "var(--ink-500)", marginTop: 4 }}>
                              {fmtNumber(p.total_responses)} responses
                            </div>
                          </div>
                        </div>
                        {isTop && (
                          <div
                            style={{
                              padding: "6px 12px",
                              borderRadius: 999,
                              background: "rgba(194, 108, 27, 0.12)",
                              color: "var(--saffron-deep)",
                              fontSize: 12,
                              fontWeight: 700,
                              letterSpacing: "0.04em",
                            }}
                          >
                            Top performer
                          </div>
                        )}
                      </div>

                      <div style={{ display: "grid", gap: 14 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, color: "var(--ink-500)" }}>
                          <span>Preferred rate</span>
                          <span style={{ fontWeight: 700, color: "var(--ink-900)" }}>{(p.preferred_rate * 100).toFixed(1)}%</span>
                        </div>
                        <div style={{ height: 8, background: "var(--ink-100)", borderRadius: 999, overflow: "hidden" }}>
                          <div
                            style={{
                              width: `${p.preferred_rate * 100}%`,
                              height: "100%",
                              background: meta.color,
                              borderRadius: 999,
                            }}
                          />
                        </div>

                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, color: "var(--ink-500)" }}>
                          <span>Avg rating</span>
                          <span style={{ fontWeight: 700, color: "var(--ink-900)" }}>{p.avg_rating.toFixed(2)}</span>
                        </div>
                        <div style={{ height: 8, background: "var(--ink-100)", borderRadius: 999, overflow: "hidden" }}>
                          <div
                            style={{
                              width: `${Math.min(p.fallback_rate * 100, 100)}%`,
                              height: "100%",
                              background: p.fallback_rate > 0.1 ? "var(--red)" : "var(--ink-400)",
                              borderRadius: 999,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Section>
      )}

      {/* ── Timeseries ───────────────────────────────────────────── */}
      <Section
        title="Daily volume"
        eyebrow="The pulse"
        right={
          <div style={{ fontSize: 13, color: "var(--ink-500)", display: "flex", alignItems: "center", gap: 6, fontWeight: 500 }}>
            <Activity size={14} />
            assistant messages vs. unanswered
          </div>
        }
      >
        {loading ? (
          <Skeleton height={340} borderRadius={8} />
        ) : !timeseries.length ? (
          <Empty>No conversations in this period yet.</Empty>
        ) : (
          <div style={{ width: "100%", height: 340 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeseries} margin={{ top: 12, right: 16, bottom: 12, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--ink-100)" vertical={false} strokeOpacity={0.8} />
                <XAxis
                  dataKey="bucket"
                  tickFormatter={fmtDateShort}
                  tick={{ fontSize: 12, fill: "var(--ink-500)" }}
                  axisLine={{ stroke: "var(--ink-200)" }}
                  tickLine={false} dy={12}
                />
                <YAxis tick={{ fontSize: 12, fill: "var(--ink-400)" }} axisLine={false} tickLine={false} dx={-8} />
                <Tooltip
                  content={<ChartTooltip />}
                  cursor={{ stroke: "var(--ink-200)", strokeWidth: 1.5, strokeDasharray: "4 4" }}
                />
                <Legend wrapperStyle={{ fontSize: 13, paddingTop: 16, fontWeight: 500, color: "var(--ink-700)" }} iconType="circle" />
                <Line
                  type="monotone" dataKey="assistant_msgs" name="Assistant replies"
                  stroke="var(--saffron)" strokeWidth={3}
                  dot={{ r: 3, fill: "var(--saffron)", strokeWidth: 0 }}
                  activeDot={{ r: 6, stroke: "#fff", strokeWidth: 2, fill: "var(--saffron)" }}
                />
                <Line
                  type="monotone" dataKey="unanswered" name="Unanswered"
                  stroke="var(--red)" strokeWidth={3}
                  dot={{ r: 3, fill: "var(--red)", strokeWidth: 0 }}
                  activeDot={{ r: 6, stroke: "#fff", strokeWidth: 2, fill: "var(--red)" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Section>

      {/* ── Language + Themes + (alpha) Method donut ─────────────── */}
      <div
        style={{
          display: "grid",
          gap: 24,
          marginTop: 32,
          gridTemplateColumns: isAlpha
            ? "repeat(auto-fit, minmax(340px, 1fr))"
            : "repeat(auto-fit, minmax(400px, 1fr))",
        }}
      >
        {/* Languages donut */}
        <div
          className="panel"
          style={{
            background: "#ffffff",
            borderRadius: 16,
            border: "1px solid var(--ink-100)",
            boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
            overflow: "visible",
          }}
        >
          <div
            className="panel-header"
            style={{ padding: "24px 28px", borderBottom: "1px solid var(--ink-100)", background: "rgba(251, 246, 238, 0.3)" }}
          >
            <div className="t-eyebrow" style={{ color: "var(--ink-500)", marginBottom: 6 }}>Bhasha vyavahar</div>
            <div className="t-display" style={{ fontSize: 24, display: "flex", alignItems: "center", gap: 10, color: "var(--ink-900)" }}>
              <Languages size={22} style={{ color: "var(--saffron-deep)" }} />
              Languages
            </div>
          </div>
          <div className="panel-body" style={{ padding: "32px" }}>
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: 300 }}>
                <Skeleton width={220} height={220} borderRadius="50%" />
              </div>
            ) : !langData.length ? (
              <Empty>No language data yet.</Empty>
            ) : (
              <>
                <div style={{ width: "100%", height: 250, position: "relative", marginBottom: 24 }}>
                  <div style={{ position: "relative", zIndex: 2, width: "100%", height: "100%" }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={langData} dataKey="value" nameKey="name"
                          innerRadius={72} outerRadius={108} paddingAngle={6} strokeWidth={0} cornerRadius={10}
                        >
                          {langData.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                        <Legend wrapperStyle={{ fontSize: 13, fontWeight: 500, marginTop: 14 }} iconType="circle" />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -58%)",
                      textAlign: "center",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  >
                    <div style={{ fontFamily: "var(--serif)", fontSize: 30, fontWeight: 700, color: "var(--ink-900)", lineHeight: 1 }}>
                      {fmtNumber(totalLangSessions)}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--ink-500)", marginTop: 8, fontWeight: 600, letterSpacing: "0.12em" }}>
                      TOTAL MESSAGES
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 18 }}>
                  {topLanguages.map(({ name, value }, i) => (
                    <div
                      key={name}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        padding: "18px 20px",
                        borderRadius: 16,
                        background: "#f8fbff",
                        border: `1px solid ${CHART_COLORS[i % CHART_COLORS.length]}22`,
                      }}
                    >
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 12,
                          background: `${CHART_COLORS[i % CHART_COLORS.length]}22`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <span style={{ fontSize: 18, fontWeight: 700, color: CHART_COLORS[i % CHART_COLORS.length] }}>
                          {name[0]}
                        </span>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: "var(--ink-500)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.12em" }}>
                          {name}
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: "var(--ink-900)", marginTop: 4 }}>
                          {fmtNumber(value)}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--ink-500)", marginTop: 5 }}>
                          {totalLangSessions ? fmtPct(value / totalLangSessions) : "0%"} of total
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Themes bar list */}
        <div
          className="panel"
          style={{
            background: "#ffffff",
            borderRadius: 16,
            border: "1px solid var(--ink-100)",
            boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
            overflow: "hidden",
          }}
        >
          <div
            className="panel-header"
            style={{ padding: "24px 28px", borderBottom: "1px solid var(--ink-100)", background: "rgba(251, 246, 238, 0.3)" }}
          >
            <div className="t-eyebrow" style={{ color: "var(--ink-500)", marginBottom: 6 }}>Vichar-prakar</div>
            <div className="t-display" style={{ fontSize: 24, color: "var(--ink-900)" }}>Themes asked about</div>
          </div>
          <div className="panel-body" style={{ padding: "28px" }}>
            {loading ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {[...Array(6)].map((_, i) => (
                  <div key={i}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <Skeleton width="40%" height={16} />
                      <Skeleton width="10%" height={16} />
                    </div>
                    <Skeleton height={8} borderRadius={4} />
                  </div>
                ))}
              </div>
            ) : !themes.length ? (
              <Empty>No themes detected yet.</Empty>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {themes.slice(0, 8).map((t, i) => {
                  const max = Math.max(...themes.map((x) => x.count));
                  const pct = max ? (t.count / max) * 100 : 0;
                  return (
                    <div key={t.theme}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 13 }}>
                        <span style={{ color: "var(--ink-800)", fontWeight: 600 }}>{themeLabel(t.theme)}</span>
                        <span style={{ color: "var(--ink-500)", fontSize: 13, fontWeight: 500 }}>{fmtNumber(t.count)}</span>
                      </div>
                      <div style={{ height: 8, background: "var(--ink-100)", borderRadius: 4, overflow: "hidden" }}>
                        <div
                          style={{
                            height: "100%",
                            width: pct + "%",
                            background: CHART_COLORS[i % CHART_COLORS.length],
                            borderRadius: 4,
                            transition: "width 600ms cubic-bezier(0.4, 0, 0.2, 1)",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Alpha-only: text vs voice donut */}
        {isAlpha && <MethodDonut stats={stats} loading={loading} />}
      </div>

      {/* ── Top topics ───────────────────────────────────────────── */}
      <Section title="Topics most asked" eyebrow="Vishay">
        {loading ? (
          <Skeleton height={360} borderRadius={8} />
        ) : !topics.length ? (
          <Empty>No topic data yet.</Empty>
        ) : (
          <div style={{ width: "100%", height: 360 }}>
            <ResponsiveContainer>
              <BarChart data={topics} layout="vertical" margin={{ top: 8, right: 32, bottom: 8, left: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--ink-100)" horizontal={false} strokeOpacity={0.7} />
                <XAxis type="number" tick={{ fontSize: 12, fill: "var(--ink-400)" }} axisLine={false} tickLine={false} />
                <YAxis
                  type="category" dataKey="topic" width={190}
                  tick={{ fontSize: 13, fill: "var(--ink-700)", fontWeight: 500 }}
                  axisLine={false} tickLine={false}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "var(--paper-2)", opacity: 0.6 }} />
                <Bar dataKey="count" fill="var(--saffron)" radius={[0, 6, 6, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Section>

      {/* ── Recent unanswered ─────────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gap: 24,
          marginTop: 32,
          gridTemplateColumns: "1fr",
          alignItems: "stretch",
        }}
      >
        <div
          className="panel"
          style={{
            background: "#ffffff",
            borderRadius: 16,
            border: "1px solid var(--ink-100)",
            boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
            overflow: "hidden",
          }}
        >
          <div
            className="panel-header"
            style={{ padding: "24px 28px", borderBottom: "1px solid var(--ink-100)", background: "rgba(251, 246, 238, 0.3)" }}
          >
            <div className="t-display" style={{ fontSize: 24, display: "flex", alignItems: "center", gap: 10, color: "var(--ink-900)" }}>
              <TrendingUp size={22} style={{ color: "var(--red)" }} />
              Recent unanswered
            </div>
          </div>
          <div className="panel-body" style={{ padding: 0 }}>
            {loading ? (
              <div>
                {[...Array(4)].map((_, i) => (
                  <div key={i} style={{ padding: "20px 24px", borderBottom: i === 3 ? "none" : "1px solid var(--ink-100)" }}>
                    <Skeleton width="100%" height={16} style={{ marginBottom: 8 }} />
                    <Skeleton width="70%" height={16} style={{ marginBottom: 16 }} />
                    <div style={{ display: "flex", gap: 12 }}>
                      <Skeleton width={60} height={20} borderRadius={6} />
                      <Skeleton width={80} height={20} borderRadius={6} />
                    </div>
                  </div>
                ))}
              </div>
            ) : !unanswered.length ? (
              <Empty>No unanswered queries — well done.</Empty>
            ) : (
              <div>
                {unanswered.map((u, i) => (
                  <div
                    key={u.message_id}
                    style={{
                      padding: "20px 24px",
                      borderBottom: i === unanswered.length - 1 ? "none" : "1px solid var(--ink-100)",
                      transition: "background 0.15s ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--paper-2)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <div
                      style={{
                        fontFamily: "var(--serif)",
                        fontSize: 16,
                        fontStyle: "italic",
                        color: "var(--ink-900)",
                        lineHeight: 1.6,
                        marginBottom: 12,
                      }}
                      className={u.language?.startsWith("hi") || u.language?.startsWith("mr") ? "devanagari" : ""}
                    >
                      "{u.query.slice(0, 160)}{u.query.length > 160 ? "…" : ""}"
                    </div>
                    <div style={{ display: "flex", gap: 12, fontSize: 12, color: "var(--ink-500)", alignItems: "center" }}>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: 6,
                          background: "var(--ink-100)",
                          color: "var(--ink-700)",
                          fontWeight: 600,
                          letterSpacing: "0.02em",
                        }}
                      >
                        {langLabel(u.language)}
                      </span>
                      <span>{fmtDateShort(u.occurred_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
